/* NPM */
import classNames from 'classnames';
import React from 'react';
/* STORES */
import CameraThumbnail from 'components/CameraThumbnail/CameraThumbnail';
/* COMPONENT LIB */
import { Checkbox, Flex, IconWithTooltip, MDIIcon } from '@solinkcorp/solink-components';
/* UTILS */
import CommonUtil from 'utils/CommonUtil';
import LocaleUtil from 'utils/LocaleUtil';
/* STYLES */
import { type LocaleDefinition } from '@solinkcorp/locale';
import { type SavePanelCamera } from '../types';
import './CameraSelector.sass';
/* ICONS */
import Close from '@mdi/svg/svg/close.svg?react';
import Info from '@mdi/svg/svg/information.svg?react';
import Plus from '@mdi/svg/svg/plus.svg?react';

export interface CameraSelectorProps {
  availableCameras: SavePanelCamera[];
  canEditOriginal?: boolean;
  cancelCameraSelector: () => void;
  cameraSelectorRef?: React.RefObject<HTMLDivElement>;
  colorMode?: 'dark' | 'light';
  debugMode?: boolean;
  getDeviceName?: (deviceId: string) => string | undefined;
  isMultiSelect?: boolean;
  locale: LocaleDefinition;
  newSelectedCameras: { [key: string]: SavePanelCamera };
  /** cameras that will be in the selected state by default and uneditable */
  originalCameras: { [key: string]: SavePanelCamera };
  submitCameraSelector: () => void;
  toggleCamSelection: (camera: SavePanelCamera) => void;
  updateCamSelection: (cameras: { [key: string]: SavePanelCamera }) => void;
  visible: boolean;
  hideOverflow?: boolean;
  setEveryAlarmCamera?: (value: boolean) => void;
  source?: string;
}

const CameraSelector: React.FC<CameraSelectorProps> = (props) => {
  const {
    locale,
    originalCameras,
    newSelectedCameras,
    availableCameras,
    hideOverflow = false,
    canEditOriginal = false,
    cancelCameraSelector,
    cameraSelectorRef,
    colorMode,
    debugMode,
    getDeviceName,
    isMultiSelect = true,
    submitCameraSelector,
    toggleCamSelection,
    updateCamSelection,
    visible = true,
    setEveryAlarmCamera,
    source,
  } = props;

  const selectAll = ({ value }: { value: boolean }) => {
    const allCameras = CommonUtil.mapCollectionToObject({
      collection: availableCameras,
      key: 'id',
    });
    updateCamSelection(
      value
        ? (allCameras as { [key: string]: SavePanelCamera })
        : canEditOriginal
          ? {}
          : originalCameras,
    );
    if (setEveryAlarmCamera) {
      setEveryAlarmCamera(value);
    }
  };

  const cameraOkButtonClass = classNames(
    'flat-button',
    'turquoise-bg',
    'white-txt',
    'camera-button',
    {
      disabled: Object.keys(newSelectedCameras).length < 1,
    },
  );

  const newCameraCount = Object.keys(newSelectedCameras).length;
  const cameraSelectorClass = classNames('camera-selector', {
    visible,
    'dark-mode': colorMode === 'dark',
  });

  const cameraSelectorContentClass = classNames('camera-selector-content', {
    'hide-overflow': hideOverflow,
  });

  const sortedCameras = React.useMemo(
    () =>
      [...availableCameras].sort((a, b) =>
        a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()),
      ),
    [availableCameras],
  );

  return (
    <div
      ref={cameraSelectorRef}
      className={cameraSelectorClass}
      data-intercom-target="camera-selector"
    >
      <div className={cameraSelectorContentClass}>
        <Flex className="top-wrapper" justifyContent="space-between">
          <Flex className="top" alignItems="center">
            {isMultiSelect && (
              <>
                <Checkbox
                  dataTestId="camera-selector-all"
                  checked={newCameraCount === availableCameras?.length}
                  labelText={
                    source === 'alarmsPage' ? (
                      <Flex alignItems="center" gap="4px">
                        {locale.alarms.alarmEnabledCameras}
                        <IconWithTooltip
                          icon={Info}
                          title={locale.alarms.alarmEnabledCamerasTooltip}
                          iconSize={16}
                          className="info-tooltip"
                        />
                      </Flex>
                    ) : (
                      locale.general.selectAll
                    )
                  }
                  onChange={selectAll}
                  name="select-all"
                />
                <div className="label-text" data-testid="total-cameras-selected">
                  {locale.camera.cameras} {newCameraCount}
                </div>
              </>
            )}
          </Flex>
          <Flex className="top" width="auto" alignItems="center">
            <div
              className="flat-button no-bg size-down-1"
              data-testid="camera-selector-cancel"
              onClick={cancelCameraSelector}
            >
              {LocaleUtil.getUpperCase(locale.general.cancel)}
            </div>
            <div
              className={cameraOkButtonClass}
              data-testid="camera-selector-ok"
              onClick={submitCameraSelector}
            >
              {LocaleUtil.getUpperCase(locale.general.ok)}
            </div>
          </Flex>
        </Flex>
        <Flex className="cameras-wrap center" flexWrap="wrap" justifyContent="flex-start">
          {sortedCameras.map((camera: SavePanelCamera, idx: number) => {
            const { id: cameraId } = camera;

            const canToggle = canEditOriginal || !originalCameras[cameraId];

            const camWrapClass = classNames('cam-wrap', {
              selected: !!newSelectedCameras[cameraId],
              'first-cam': idx % 4 === 0,
              'last-cam': (idx + 5) % 4 === 0,
            });

            const camOverlayClass = classNames('overlay', {
              'toggle-enabled': canToggle,
            });

            return (
              <div key={`available-cam-${idx}`} className={camWrapClass} data-testid={camera.name}>
                {canToggle ? (
                  <Flex
                    className={camOverlayClass}
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={() => toggleCamSelection(camera)}
                  >
                    <div>
                      {camera.name}
                      {debugMode && getDeviceName && (
                        <IconWithTooltip
                          className="debug-info-icon"
                          title={getDeviceName(camera.deviceId) || ''}
                          icon={Info}
                          iconSize={12}
                          fill="var(--color-white)"
                        />
                      )}
                    </div>
                    {isMultiSelect && !newSelectedCameras[cameraId] && (
                      <MDIIcon icon={Plus} size={18} fill="var(--color-white)" />
                    )}
                    {isMultiSelect && !!newSelectedCameras[cameraId] && (
                      <MDIIcon icon={Close} size={18} fill="var(--color-white)" />
                    )}
                  </Flex>
                ) : (
                  <Flex className="overlay" justifyContent="space-between">
                    <div>
                      {camera.name}
                      {debugMode && getDeviceName && (
                        <IconWithTooltip
                          className="debug-info-icon"
                          title={getDeviceName(camera.deviceId) || ''}
                          icon={Info}
                          iconSize={12}
                          fill="var(--color-white)"
                        />
                      )}
                    </div>
                  </Flex>
                )}
                <CameraThumbnail cameraId={camera.chId || camera.id} />
              </div>
            );
          })}
        </Flex>
      </div>
    </div>
  );
};

export default CameraSelector;
